import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { RefreshCw, ArrowUpRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useStore } from 'zustand';
import { useAuth } from '../../../services/authentication/authContext';
import { useSubscription } from '../../../hooks/useSubscription';
import { getImpersonationUserId, useImpersonationStore } from '../../../stores/appImpersonationStore';
import { useRestaurantStore } from '../../../stores/appRestaurantStore';
import { canAccessRecipeViewer } from './recipeViewerAccess';
import { RecipeViewerContext } from './RecipeViewerContext';
import { createRecipeViewerStore } from './recipeViewerStore';
import { ViewerLink, ViewerNavigationProvider, useViewerLandingPath } from './viewerNavigation';
import { RecipeViewerGrid, RecipeViewerDetail, RecipeViewerComponent } from './RecipeViewerRecipes';
import { RecipeViewerMatrix, RecipeViewerAllergySearch, RecipeViewerAllergenDetail } from './RecipeViewerAllergies';
import LanguageToggle from '../../common/LanguageToggle';
import ThemeToggle from '../../common/ThemeToggle';
import './recipeViewer.css';

export default function RecipeViewer() {
  const { restaurantId } = useParams();
  const { user, backendUser, accountSettings, loading } = useAuth();
  const { isActive, loading: subscriptionLoading } = useSubscription();
  const target = useImpersonationStore(s => s.target);
  const [identityChanged, setIdentityChanged] = useState(false);
  const [profileTimedOut, setProfileTimedOut] = useState(false);
  const { t } = useTranslation();
  const location = useLocation();
  const capturedTarget = target?.userId ?? null;

  useEffect(() => {
    const check = () => setIdentityChanged(getImpersonationUserId() !== capturedTarget);
    window.addEventListener('storage', check);
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', check);
    return () => {
      window.removeEventListener('storage', check);
      window.removeEventListener('focus', check);
      document.removeEventListener('visibilitychange', check);
    };
  }, [capturedTarget]);

  useEffect(() => {
    setProfileTimedOut(false);
    if (loading || !user || backendUser) return;
    // Shared auth has no profile-error state: avoid an indefinite spinner after /me fails.
    const timer = window.setTimeout(() => setProfileTimedOut(true), 10000);
    return () => window.clearTimeout(timer);
  }, [loading, user, backendUser]);

  if (identityChanged) return <ViewerMessage message={t('recipeViewer.sessionChanged')} reload />;
  if (loading) return <ViewerMessage message={t('recipeViewer.loading')} />;
  if (!user) return <Navigate to="/login" replace state={{ recipeViewerReturnTo: location.pathname + location.search }} />;
  if (!backendUser && profileTimedOut) return <ViewerMessage message={t('recipeViewer.profileFailed')} reload />;
  if (!backendUser || subscriptionLoading) return <ViewerMessage message={t('recipeViewer.loading')} />;
  if (!isActive) return <Navigate to="/payment" replace />;
  if (!canAccessRecipeViewer(restaurantId, backendUser, accountSettings)) {
    return <ViewerMessage message={t('recipeViewer.unavailable')} />;
  }
  return <ViewerNavigationProvider key={`${user.id}:${backendUser.id}:${capturedTarget}:${restaurantId}`}>
    <ViewerData restaurantId={restaurantId!} targetId={capturedTarget} />
  </ViewerNavigationProvider>;
}

function ViewerMessage({ message, reload = false }: { message: string; reload?: boolean }) {
  const { t } = useTranslation();
  return <div className="recipe-viewer rv-message"><p role="status">{message}</p>
    {reload && <button className="rv-button" onClick={() => window.location.reload()}>{t('recipeViewer.reload')}</button>}
    <Link to="/recipes" className="rv-button">{t('recipeViewer.returnDashboard')}</Link>
  </div>;
}

function ViewerData({ restaurantId, targetId }: { restaurantId: string; targetId: string | null }) {
  const store = useMemo(() => createRecipeViewerStore(restaurantId, () => getImpersonationUserId() === targetId), [restaurantId, targetId]);
  const data = useStore(store);
  const { t } = useTranslation();
  const location = useLocation();
  const restaurantName = useRestaurantStore(s => s.restaurants.find(restaurant => restaurant.id === restaurantId)?.name);
  const base = `/recipe-viewer/${restaurantId}`;
  const inAllergies = location.pathname.startsWith(`${base}/allergies/`);

  useEffect(() => {
    void store.getState().refresh();
    const refresh = () => { if (document.visibilityState === 'visible') void store.getState().refresh(); };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
      store.getState().clear();
    };
  }, [store]);
  useEffect(() => { if (inAllergies) void store.getState().refresh(); }, [store, inAllergies, location.pathname]);

  const activeArea = location.pathname.includes('/allergies/search') ? 'search'
    : location.pathname.includes('/allergies/recipes/')
      ? (location.state?.viewerFrom?.includes('/allergies/search') ? 'search' : 'matrix')
      : inAllergies ? 'matrix' : 'recipes';

  return <RecipeViewerContext.Provider value={{ restaurantId, recipes: data.recipes, isStale: data.isStale,
    isRefreshing: data.isRefreshing, lastUpdated: data.lastUpdated, refresh: data.refresh }}>
    <div className="recipe-viewer">
      <header className="rv-toolbar">
        <span className="rv-restaurant">{restaurantName || t('recipeViewer.title')}</span>
        <nav className="rv-tabs" aria-label={t('recipeViewer.navigation')}>
          <ViewerTab path={`${base}/recipes`} active={activeArea === 'recipes'} label={t('recipeViewer.recipes')} />
          <ViewerTab path={`${base}/allergies/matrix`} active={activeArea === 'matrix'} label={t('recipeViewer.matrix')} />
          <ViewerTab path={`${base}/allergies/search`} active={activeArea === 'search'} label={t('recipeViewer.allergySearch')} />
        </nav>
        <div className="rv-toolbar-actions">
          <LanguageToggle className="rv-button" label={t('recipeViewer.toggleLanguage')} />
          <ThemeToggle className="rv-button" label={t('recipeViewer.toggleTheme')} />
          <button type="button" className="rv-button" onClick={() => void data.refresh()} disabled={data.isRefreshing}
            aria-label={t('recipeViewer.refresh')} title={t('recipeViewer.refresh')}>
            <RefreshCw size={18} className={data.isRefreshing ? 'rv-spinning' : ''} aria-hidden="true" />
          </button>
          <Link to="/recipes" className="rv-button" aria-label={t('recipeViewer.returnDashboard')}>
            <ArrowUpRight size={18} aria-hidden="true" /><span>{t('recipeViewer.returnDashboard')}</span>
          </Link>
        </div>
      </header>
      {data.isStale && data.status === 'loaded' && <div className="rv-data-status" role="status" aria-live="polite">
        <span className="rv-stale">{t('recipeViewer.stale')}
          <button className="rv-button" disabled={data.isRefreshing} onClick={() => void data.refresh()}>{t('recipeViewer.retry')}</button>
        </span>
      </div>}
      <div id="recipeViewerScroll" className="rv-scroll">
        {data.status === 'loading' ? <p className="rv-page">{t('recipeViewer.loading')}</p>
          : data.status === 'error' || data.status === 'denied' ? <div className="rv-page" role="alert">
            <p>{t(data.status === 'denied' ? 'recipeViewer.accessDenied' : 'recipeViewer.loadFailed')}</p>
            <button className="rv-button" disabled={data.isRefreshing} onClick={() => void data.refresh()}>{t('recipeViewer.retry')}</button>
          </div>
          : <Routes>
            <Route index element={<Navigate to="recipes" replace />} />
            <Route path="recipes" element={<RecipeViewerGrid />} />
            <Route path="recipes/:recipeId" element={<RecipeViewerDetail />} />
            <Route path="recipes/:recipeId/components/:componentKey" element={<RecipeViewerComponent />} />
            <Route path="allergies/matrix" element={<RecipeViewerMatrix />} />
            <Route path="allergies/search" element={<RecipeViewerAllergySearch />} />
            <Route path="allergies/recipes/:recipeId" element={<RecipeViewerAllergenDetail />} />
            <Route path="*" element={<Navigate to={`${base}/recipes`} replace />} />
          </Routes>}
      </div>
    </div>
  </RecipeViewerContext.Provider>;
}

function ViewerTab({ path, active, label }: { path: string; active: boolean; label: string }) {
  const destination = useViewerLandingPath(path);
  return <ViewerLink to={destination} aria-current={active ? 'page' : undefined} className={`rv-tab${active ? ' rv-tab-active' : ''}`}>{label}</ViewerLink>;
}
